import { useEffect, useRef } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { formatLocation } from "../utils/locations";
import { formatItemWithProdNeeded } from "../utils/prodNeeded";

export function LocationCard({
  location,
  onClick,
  onDelete,
  onQuickActions,
  showItemName = false,
  selected = false,
  highlighted = false,
  compact = false,
  prodNeeded = null,
  hasDroppedSibling = false,
}) {
  const buttonRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const delayedClickTimerRef = useRef(null);
  const lastTapRef = useRef(0);

  const className = [
    "location-card",
    location.is_dropped ? "is-dropped" : "",
    hasDroppedSibling && !location.is_dropped ? "has-dropped-sibling" : "",
    selected ? "selected-location-card" : "",
    highlighted ? "highlighted" : "",
    compact ? "compact" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const clearLongPress = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const clearDelayedClick = () => {
    if (delayedClickTimerRef.current) {
      window.clearTimeout(delayedClickTimerRef.current);
      delayedClickTimerRef.current = null;
    }
  };

  useEffect(() => () => {
    clearLongPress();
    clearDelayedClick();
  }, []);

  const startLongPress = () => {
    if (!onQuickActions) {
      return;
    }
    longPressTriggeredRef.current = false;
    clearLongPress();
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = true;
      clearDelayedClick();
      if (navigator.vibrate) {
        navigator.vibrate(18);
      }
      onQuickActions(location, buttonRef.current, { waitForRelease: true });
    }, 520);
  };

  const openQuickActions = () => {
    clearDelayedClick();
    lastTapRef.current = 0;
    if (navigator.vibrate) {
      navigator.vibrate(10);
    }
    onQuickActions(location, buttonRef.current);
  };

  const handleClick = () => {
    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }
    if (onQuickActions) {
      const now = Date.now();
      if (now - lastTapRef.current < 320) {
        openQuickActions();
        return;
      }
      lastTapRef.current = now;
      clearDelayedClick();
      delayedClickTimerRef.current = window.setTimeout(() => {
        delayedClickTimerRef.current = null;
        lastTapRef.current = 0;
        onClick(location);
      }, 260);
      return;
    }
    onClick(location);
  };

  return (
    <div className="location-card-shell">
      <button
        ref={buttonRef}
        type="button"
        className={className}
        onClick={handleClick}
        onPointerDown={startLongPress}
        onPointerUp={clearLongPress}
        onPointerLeave={clearLongPress}
        onPointerCancel={clearLongPress}
        onContextMenu={(event) => {
          if (onQuickActions) {
            event.preventDefault();
            onQuickActions(location, buttonRef.current, { waitForRelease: false });
          }
        }}
        onDoubleClick={(event) => {
          if (onQuickActions) {
            event.preventDefault();
            openQuickActions();
          }
        }}
      >
        {location.is_dropped && <span className="dropped-strip">DROPPED</span>}
        {location.note && <span className="note-dot" aria-label="Has note" />}
        {location.partial_count && (
          <span className="partial-badge" aria-label={`Partial ${location.partial_count}`}>
            {location.partial_count}
          </span>
        )}
        {selected && (
          <span className="selected-badge" aria-label="Selected location">
            <CheckCircleIcon fontSize="small" />
          </span>
        )}
        <span className="code">{formatLocation(location)}</span>
        {showItemName && (
          <span className="subtitle">
            {formatItemWithProdNeeded(location.item_name, prodNeeded)}
          </span>
        )}
      </button>
      {onDelete && (
        <button
          type="button"
          className="card-delete-button"
          aria-label={`Delete location ${formatLocation(location)}`}
          onClick={() => onDelete(location)}
        >
          <CloseIcon fontSize="small" />
        </button>
      )}
    </div>
  );
}
